import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/organization/device-employees"
)({
  component: RouteComponent,
});

function RouteComponent() {
  // Device-employee mappings are attendance-hardware configuration, gated by
  // the same capability as the Devices page.
  return (
    <ProtectedRoute requiredPermission="attendance.policy.manage">
      <DeviceEmployeesDataTable />
    </ProtectedRoute>
  );
}

import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";

import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { DeviceEmployeeFormSheet } from "@/components/crud/forms/DeviceEmployeeFormSheet";
import { DeviceEmployeeTable } from "@/components/crud/table/DeviceEmployeeTable";

// Hooks
import useFetchDeviceEmployees, {
  type DeviceEmployeeOrdering,
} from "@/hooks/api/organization/devices/device-employees/useFetchDeviceEmployees";
import useCreateDeviceEmployee from "@/hooks/api/organization/devices/device-employees/useCreateDeviceEmployee";
import useUpdateDeviceEmployee from "@/hooks/api/organization/devices/device-employees/useUpdateDeviceEmployee";
import useDeleteDeviceEmployee from "@/hooks/api/organization/devices/device-employees/useDeleteDeviceEmployee";
import useFetchDevices from "@/hooks/api/organization/devices/useFetchDevices";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";

import type {
  DeviceEmployee,
  DeviceEmployeePayload,
} from "@/types/organization/device-employees";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";

function DeviceEmployeesDataTable() {
  // UI State
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingDeviceEmployee, setEditingDeviceEmployee] =
    useState<DeviceEmployee | null>(null);
  const [deviceEmployeeToDelete, setDeviceEmployeeToDelete] =
    useState<DeviceEmployee | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Ordering
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        device__serial_number: SortDirection;
        user__username: SortDirection;
        device_user_id: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      DeviceEmployeeOrdering
    >({
      device__serial_number: null,
      user__username: null,
      device_user_id: null,
      created_at: null,
      updated_at: null,
    });

  // Filters
  const [activeFilter, setActiveFilter] = useState<
    "true" | "false" | "clear" | undefined
  >();
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);
  const [showFilters, setShowFilters] = useState(false);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // CRUD Hooks
  const deviceEmployeesQuery = useFetchDeviceEmployees({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      activeFilter === "true"
        ? true
        : activeFilter === "false"
          ? false
          : undefined,
  });
  const deviceEmployees = deviceEmployeesQuery.data?.results ?? [];
  const count = deviceEmployeesQuery.data?.count ?? 0;

  const createDeviceEmployee = useCreateDeviceEmployee();
  const updateDeviceEmployee = useUpdateDeviceEmployee();
  const deleteDeviceEmployee = useDeleteDeviceEmployee();

  // For device select in form — fetch when the create/edit sheet is open
  const devicesQuery = useFetchDevices(
    { page_size: pageSize, page: 1 },
    { enabled: isSheetOpen },
  );
  const devices = devicesQuery.data?.results ?? [];

  // For user autocomplete in form
  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const isMutating =
    createDeviceEmployee.isPending ||
    updateDeviceEmployee.isPending ||
    deleteDeviceEmployee.isPending;
  const isLoading =
    deviceEmployeesQuery.isPending || isMutating || devicesQuery.isPending;
  const isFetching =
    deviceEmployeesQuery.isFetching || isMutating || devicesQuery.isFetching;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, activeFilter, setPage]);

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const resetFilters = () => {
    setOrdering({
      device__serial_number: null,
      user__username: null,
      device_user_id: null,
      created_at: null,
      updated_at: null,
    });
    setActiveFilter(undefined);
    setGlobalFilter("");
    resetDebounce();
    setPage(1);
  };

  // Handlers
  const handleCreate = () => {
    setEditingDeviceEmployee(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (deviceEmployee: DeviceEmployee) => {
    setEditingDeviceEmployee(deviceEmployee);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (deviceEmployee: DeviceEmployee) => {
    setEditingDeviceEmployee(deviceEmployee);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (deviceEmployee: DeviceEmployee) => {
    setDeviceEmployeeToDelete(deviceEmployee);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deviceEmployeeToDelete) return;
    try {
      await deleteDeviceEmployee.mutateAsync({ id: deviceEmployeeToDelete.id });
      toast.success("Assignment deleted successfully!");
      setIsDeleteDialogOpen(false);
      setDeviceEmployeeToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: DeviceEmployeePayload) => {
    if (editingDeviceEmployee) {
      await updateDeviceEmployee.mutateAsync({
        id: editingDeviceEmployee.id,
        payload: data,
      });
      toast.success("Assignment updated successfully!");
    } else {
      await createDeviceEmployee.mutateAsync(data);
      toast.success("Assignment created successfully!");
    }

    setIsSheetOpen(false);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Assign Device User"
          description="Manage employee assignments to attendance devices here."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleCreate}
          addLabel="Add Assignment"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search assignments..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8 max-w-sm font-light"
            />
          </div>
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={activeFilter}
            onChange={(value) => {
              if (value === "clear") {
                setActiveFilter(undefined);
              } else {
                setActiveFilter(value);
              }
            }}
          />
        </FiltersPanel>

        <DeviceEmployeeTable
          deviceEmployees={deviceEmployees}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <DeviceEmployeeFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingDeviceEmployee={editingDeviceEmployee}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
          autoCompleteLoading={autoCompleteLoading}
          devices={devices}
          users={users}
          onSearchUsers={handleSearchUsers}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Assignment"
          description={`Are you sure you want to remove the assignment for "${deviceEmployeeToDelete?.user_name}"? This action cannot be undone.`}
          itemName={deviceEmployeeToDelete?.user_name}
          isLoading={deleteDeviceEmployee.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
